import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Search,
  Mail,
  Phone,
  Building2,
  Trash2,
  Check,
  FileText,
  X,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

const STATUSES = [
  { key: "new",         label: "New" },
  { key: "in_progress", label: "In Progress" },
  { key: "won",         label: "Won" },
  { key: "lost",        label: "Lost" },
];

const statusStyles = {
  new:         "bg-[#A4161A]/12 text-[#A4161A] border-[#A4161A]/30",
  in_progress: "bg-[#9A9A9A]/15 text-[#FAFAFA] border-[#9A9A9A]/30",
  won:         "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  lost:        "bg-[#444444] text-[#9A9A9A] border-[#9A9A9A]/25",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminDashboard() {
  const { user, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get("/quotes");
      setQuotes(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load quotes";
      toast.error(msg);
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line
  }, [user]);

  const filtered = useMemo(() => {
    return quotes.filter((q) => {
      if (filter !== "all" && (q.status || "new") !== filter) return false;
      if (!query.trim()) return true;
      const needle = query.toLowerCase();
      return [q.name, q.company, q.email, q.phone, q.solution, q.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [quotes, query, filter]);

  const stats = useMemo(() => {
    const t = quotes.length;
    const byStatus = quotes.reduce((acc, q) => {
      const s = q.status || "new";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    const last7 = quotes.filter((q) => {
      if (!q.created_at) return false;
      const d = new Date(q.created_at);
      return (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
    }).length;
    return { total: t, new: byStatus.new || 0, won: byStatus.won || 0, last7 };
  }, [quotes]);

  const updateStatus = async (id, status) => {
    try {
      await authAxios.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update");
    }
  };

  const removeQuote = async (id) => {
    if (!window.confirm("Delete this quote submission? This cannot be undone.")) return;
    try {
      await authAxios.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Quote deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to delete");
    }
  };

  if (user === undefined) return null;
  if (user === null) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#222222] text-[#FAFAFA]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-[#9A9A9A]/15 bg-[#222222]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
          <a href="/" className="inline-flex items-center gap-3 text-[#FAFAFA] text-[15px]" data-testid="admin-logo-home">
            <img src="/brand/logo.png" alt="DB Computronics" className="w-9 h-9 rounded-md object-contain" />
            <span className="hidden sm:inline font-display tracking-tight">Computronics<span className="text-[#A4161A]">.</span></span>
            <span className="ml-3 text-[10px] tracking-[0.3em] uppercase text-[#9A9A9A] font-btn">Admin</span>
          </a>
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <div className="text-[12px] text-[#FAFAFA]">{user.email}</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#9A9A9A] font-btn">{user.role}</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/admin/login", { replace: true }); }}
              className="btn-outline inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-btn text-[11px] tracking-[0.22em] uppercase"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} className="relative z-10" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {/* Title + refresh */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="section-label mb-3">Quote Submissions</div>
            <h1 className="font-display text-[36px] sm:text-[44px] leading-[1.02] tracking-[-0.03em]">
              Inbox<span className="text-[#A4161A]">.</span>
            </h1>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#9A9A9A]/30 text-[#FAFAFA] hover:border-[#A4161A] hover:text-[#A4161A] transition-colors duration-500 font-btn text-[11px] tracking-[0.22em] uppercase"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            ["Total", stats.total],
            ["New", stats.new],
            ["Last 7 days", stats.last7],
            ["Won", stats.won],
          ].map(([k, v], i) => (
            <div key={k} className="bg-[#FAFAFA] text-[#222222] rounded-xl p-6 border border-[#9A9A9A]/15" data-testid={`stat-${k.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#9A9A9A] font-btn mb-2">{k}</div>
              <div className="font-display text-[36px] leading-none tracking-tight">{v}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap mb-6">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, company, email…"
              className="w-full bg-[#FAFAFA] text-[#222222] placeholder:text-[#9A9A9A] rounded-lg border border-[#9A9A9A]/20 pl-10 pr-4 py-3 text-[14px] outline-none focus:border-[#A4161A] transition-colors"
              data-testid="admin-search"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[{ key: "all", label: "All" }, ...STATUSES].map((s) => (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`px-4 py-2 rounded-lg border text-[11px] tracking-[0.22em] uppercase font-btn transition-colors duration-300 ${
                  filter === s.key
                    ? "bg-[#A4161A] border-[#A4161A] text-[#FAFAFA]"
                    : "bg-transparent border-[#9A9A9A]/25 text-[#FAFAFA] hover:border-[#A4161A]"
                }`}
                data-testid={`filter-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#FAFAFA] text-[#222222] rounded-xl border border-[#9A9A9A]/15 overflow-hidden" data-testid="admin-quotes-table">
          {loading ? (
            <div className="p-12 text-center text-[#9A9A9A] font-body text-[14px]">Loading submissions…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <FileText size={28} className="mx-auto mb-4 text-[#9A9A9A]" />
              <div className="font-display text-[20px] mb-1">No submissions yet</div>
              <div className="text-[#9A9A9A] font-body text-[13px]">
                {quotes.length === 0 ? "Quotes will appear here once customers submit the contact form." : "No results match your filter."}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-[#f0f0f0] text-[#9A9A9A] uppercase text-[10px] tracking-[0.22em]">
                  <tr>
                    <th className="text-left px-5 py-3 font-btn">Contact</th>
                    <th className="text-left px-5 py-3 font-btn hidden md:table-cell">Company</th>
                    <th className="text-left px-5 py-3 font-btn hidden lg:table-cell">Solution</th>
                    <th className="text-left px-5 py-3 font-btn">Status</th>
                    <th className="text-left px-5 py-3 font-btn hidden md:table-cell">Received</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr
                      key={q.id}
                      className="border-t border-[#9A9A9A]/15 hover:bg-[#f7f7f7] cursor-pointer transition-colors"
                      onClick={() => setSelected(q)}
                      data-testid={`quote-row-${q.id}`}
                    >
                      <td className="px-5 py-4">
                        <div className="font-btn text-[#222222] text-[14px]">{q.name}</div>
                        <div className="text-[#9A9A9A] text-[12px] mt-0.5">{q.email}</div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell text-[#444444]">{q.company || "—"}</td>
                      <td className="px-5 py-4 hidden lg:table-cell">
                        {q.solution ? (
                          <span className="inline-block px-2.5 py-1 rounded-md bg-[#222222]/5 text-[#222222] text-[11px] tracking-[0.05em]">
                            {q.solution}
                          </span>
                        ) : <span className="text-[#9A9A9A]">—</span>}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-block px-2.5 py-1 rounded-md border text-[10px] tracking-[0.2em] uppercase font-btn ${statusStyles[q.status || "new"]}`}>
                          {(q.status || "new").replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell text-[#9A9A9A] text-[12px]">{fmt(q.created_at)}</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); removeQuote(q.id); }}
                          className="p-2 rounded-md text-[#9A9A9A] hover:text-[#A4161A] hover:bg-[#A4161A]/8 transition-colors"
                          aria-label="Delete quote"
                          data-testid={`delete-${q.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail panel */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="quote-detail-drawer">
          <div
            className="flex-1 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
            aria-hidden
          />
          <aside className="w-full sm:w-[540px] lg:w-[600px] bg-[#FAFAFA] text-[#222222] h-full overflow-y-auto animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between px-8 py-6 border-b border-[#9A9A9A]/15">
              <div className="section-label !text-[#9A9A9A]">Quote Detail</div>
              <button
                onClick={() => setSelected(null)}
                className="p-2 rounded-md text-[#9A9A9A] hover:text-[#A4161A] hover:bg-[#A4161A]/8 transition-colors"
                aria-label="Close"
                data-testid="quote-detail-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-8 py-8 space-y-8">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#9A9A9A] font-btn mb-2">From</div>
                <div className="font-display text-[26px] leading-tight tracking-tight">{selected.name}</div>
                {selected.company && (
                  <div className="flex items-center gap-2 mt-2 text-[#444444] text-[13px]">
                    <Building2 size={14} /> <span>{selected.company}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1.5 text-[#444444] text-[13px]">
                  <Mail size={14} /> <a href={`mailto:${selected.email}`} className="red-underline">{selected.email}</a>
                </div>
                <div className="flex items-center gap-2 mt-1.5 text-[#444444] text-[13px]">
                  <Phone size={14} /> <a href={`tel:${selected.phone}`} className="red-underline">{selected.phone}</a>
                </div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#9A9A9A] font-btn mt-3">
                  Received {fmt(selected.created_at)}
                </div>
              </div>

              {selected.solution && (
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#9A9A9A] font-btn mb-2">Solution of Interest</div>
                  <div className="inline-block px-3 py-1.5 rounded-md bg-[#222222] text-[#FAFAFA] text-[12px]">
                    {selected.solution}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#9A9A9A] font-btn mb-2">Message</div>
                <div className="bg-white border-l-2 border-[#A4161A] rounded-r-md p-4 text-[#222222] text-[14px] leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>

              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#9A9A9A] font-btn mb-3">Update Status</div>
                <div className="grid grid-cols-2 gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => updateStatus(selected.id, s.key)}
                      className={`px-3 py-2.5 rounded-md text-[10px] tracking-[0.18em] uppercase font-btn border transition-colors duration-300 inline-flex items-center justify-center gap-2 ${
                        (selected.status || "new") === s.key
                          ? "bg-[#A4161A] text-[#FAFAFA] border-[#A4161A]"
                          : "bg-transparent text-[#222222] border-[#9A9A9A]/30 hover:border-[#A4161A]"
                      }`}
                      data-testid={`set-status-${s.key}`}
                    >
                      {(selected.status || "new") === s.key && <Check size={12} />}
                      <span>{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#9A9A9A]/15">
                <a
                  href={`mailto:${selected.email}?subject=Re:%20Your%20DB%20Computronics%20enquiry`}
                  className="btn-red inline-flex items-center gap-2 px-5 py-3 rounded-lg font-btn text-[11px] tracking-[0.22em] uppercase"
                  data-testid="reply-email-cta"
                >
                  <Mail size={14} className="relative z-10" />
                  <span>Reply by Email</span>
                </a>
                <button
                  onClick={() => removeQuote(selected.id)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#9A9A9A]/30 text-[#222222] hover:border-[#A4161A] hover:text-[#A4161A] transition-colors duration-500 font-btn text-[11px] tracking-[0.22em] uppercase"
                  data-testid="delete-quote-detail"
                >
                  <Trash2 size={14} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
